import { useMemo, useState } from "react";
import { GALLERY_STORAGE_KEYS } from "../gallery/keys";
import { galleryOrder, gallerySolutionsUpToDate, maxOpenGalleryTokens, visibleGallerySets, } from "../gallery/list";
import { galleryPlanDone } from "../gallery/planner";
import { storage } from "../storage";
import { useGalleryOrder } from "./useGalleryList";
import { useGalleryPlan } from "./useGalleryPlan";
/**
 * The Gallery list page's state on both platforms, on top of `useGalleryList`:
 * the picked category (All by default), the frozen order and filtered sets,
 * the planner and its open state, and the Build step. Login and Credits
 * gating, filters' own state and layout stay with each app.
 */
export const useGalleryListPage = ({ list, categorySlug, status, chosenSort, searchTerm, allLabel, clubImported, syncRunning, }) => {
    const { categories, progress, gradesBySetId } = list;
    const planner = useGalleryPlan((planned) => ({
        completedAtBuild: Object.fromEntries([...planned.keys()].map((id) => [
            id,
            progress.get(id)?.completedGrade ?? null,
        ])),
        orderedIds: galleryOrder({
            categories,
            gradesBySetId,
            progress,
            sort: "recommended",
            plannedGrades: planned,
            coins: list.limits.coins,
        }).orderedIds,
    }));
    const { plan, plannedGrades } = planner;
    const [plannerOpen, setPlannerOpen] = useState(() => storage.get(GALLERY_STORAGE_KEYS.plannerOpen) === "true");
    const [plannerSeen, setPlannerSeen] = useState(() => storage.get(GALLERY_STORAGE_KEYS.plannerSeen) === "1");
    const togglePlanner = () => {
        setPlannerOpen(!plannerOpen);
        storage.set(GALLERY_STORAGE_KEYS.plannerOpen, String(!plannerOpen));
        if (plannerSeen)
            return;
        setPlannerSeen(true);
        storage.set(GALLERY_STORAGE_KEYS.plannerSeen, "1");
    };
    // "All" is id 0, without a slug.
    const allCategory = useMemo(() => ({
        id: 0,
        name: allLabel,
        slug: "",
        sets: categories.flatMap((c) => c.sets),
    }), [categories, allLabel]);
    const category = (categorySlug && categories.find((c) => c.slug === categorySlug)) ||
        allCategory;
    const categorySlugBySetId = useMemo(() => new Map(categories.flatMap((c) => c.sets.map((s) => [s.id, c.slug]))), [categories]);
    // An active plan always sorts on Recommended; the chosen sort comes back with it cleared.
    const sort = plannedGrades ? "recommended" : chosenSort;
    // Frozen between re-sorts: a load, a new sort, category or status, a plan, or each poll of a run.
    const order = useGalleryOrder({
        categories,
        gradesBySetId,
        progress,
        sort,
        plannedGrades,
        coins: list.limits.coins,
        resortOn: [list.resortVersion, category.id, status, plan],
    });
    const { sortPicks, completedIds } = order;
    // A plan keeps the order it was built in, whatever is completed since.
    const orderedIds = plan ? plan.orderedIds : order.orderedIds;
    // Memoized: the page re-renders on every tick of a run's count.
    const { visible, notInPlan, statusCounts } = useMemo(() => visibleGallerySets({
        sets: category.sets,
        searchTerm,
        plannedGrades,
        status,
        completedIds,
        orderedIds,
    }), [category, searchTerm, plannedGrades, status, completedIds, orderedIds]);
    const { upToDate, toSolveCount } = useMemo(() => gallerySolutionsUpToDate(category.sets, progress), [category, progress]);
    const maxOpenTokens = useMemo(() => maxOpenGalleryTokens(category.sets, gradesBySetId, progress), [category, gradesBySetId, progress]);
    const planDone = useMemo(() => plan && galleryPlanDone(plan, allCategory.sets, progress), [plan, allCategory, progress]);
    const buildDone = upToDate && !list.runShown && !list.starting;
    const build = {
        done: buildDone,
        running: list.runShown || list.starting,
        // Until the backend answers, the sets the run covers by the same rule (S-completed skipped).
        progress: list.starting
            ? { done: 0, total: toSolveCount }
            : { done: list.shownDone, total: list.run?.total ?? 0 },
        disabled: syncRunning || !clubImported,
        built: list.builtAnySolution,
        newCollectedCount: list.newCollectedCount,
    };
    return {
        planner,
        /** The plan's sets and tokens completed since it was built. */
        planDone,
        plannerOpen,
        togglePlanner,
        /** Says "Fine-tune" on the folded planner until it's first opened. */
        plannerHighlight: buildDone && !plannerSeen,
        allCategory,
        category,
        categorySlugBySetId,
        sort,
        sortPicks,
        visible,
        /** Under an active plan without a search: the sets left out of it, below a divider. */
        notInPlan,
        statusCounts,
        maxOpenTokens,
        build,
        /** What a Build on this page solves. */
        solveScope: category.id ? { categoryId: category.id } : {},
    };
};
//# sourceMappingURL=useGalleryListPage.js.map